import { publicClient } from './api';

export const MAX_TICKETS_PER_PURCHASE = 10;
export const CHECKOUT_SESSION_STORAGE_KEY = 'checkoutSessionId';
export const CHECKOUT_EXPIRY_STORAGE_KEY = 'checkoutSessionExpiry';
export const CHECKOUT_EMAIL_STORAGE_KEY = 'checkoutCustomerEmail';
export const CHECKOUT_NAME_STORAGE_KEY = 'checkoutCustomerName';

export const PLATFORM_FEE_RATE = 0.07; // 7% platform fee
export const STRIPE_FEE_RATE = 0.029; // 2.9% + $0.30
export const STRIPE_FIXED_FEE = 0.3; // $0.30 fixed fee

export interface CheckoutTicketSelection {
  id: number;
  quantity: number;
}

export interface FreeBookingTicketSelection {
  ticket_id: number;
  quantity: number;
}

export interface CheckoutRecipient {
  name: string;
  email: string;
  ticket_id?: number | null;
  quantity?: number;
}

export interface ActiveSessionInfo {
  session_id: string;
  event_id?: number;
  email?: string;
  created_at?: string;
  amount_total?: number;
  expires_at?: string;
}

export interface CheckoutSessionResponse {
  success?: boolean;
  message?: string;
  clientSecret?: string;
  client_secret?: string;
  publishableKey?: string;
  publishable_key?: string;
  sessionId?: string;
  session_id?: string;
  reservationExpiresAt?: string;
  expires_at?: number;
  payment_status?: string;
  status?: string;
  amount_total?: number;
  total_amount?: number;
  customer_email?: string;
  active_session?: ActiveSessionInfo;
  activeSession?: ActiveSessionInfo;
  event_title?: string;
  event_date?: string;
  event_timezone?: string;
  tickets?: Array<{
    ticket_type: string;
    quantity: number;
  }>;
}

export interface CreateCheckoutSessionPayload {
  event_id: number;
  tickets: CheckoutTicketSelection[];
  email: string;
  name: string;
  recipients?: CheckoutRecipient[];
  return_url?: string;
  recaptcha_token?: string;
  invite_token?: string | null;
  promo_code?: string | null;
  discount_amount?: number;
  discounted_subtotal?: number;
}

export interface CreateFreeBookingPayload {
  tickets: FreeBookingTicketSelection[];
  customer_email: string;
  customer_name: string;
  recipients?: CheckoutRecipient[];
  recaptcha_token?: string;
  invite_token?: string | null;
  promo_code?: string | null;
}

export interface FreeBookingResponse {
  success?: boolean;
  message?: string;
  redirect_url?: string;
  redirectUrl?: string;
  booking_id?: string | number;
  confirmation_number?: string;
}

export interface FreeBookingEligibilityResponse {
  all_free?: boolean;
  success?: boolean;
  message?: string;
}

export interface PromoCodeCartItem {
  ticket_id: number;
  quantity: number;
}

export interface PromoCodeValidationResponse {
  success: boolean;
  valid?: boolean;
  message?: string;
  promo_code?: string;
  subtotal?: string | number;
  discount_amount?: string | number;
  total_after_discount?: string | number;
  discount_percentage?: number;
  discount_type?: 'percentage' | 'fixed' | string;
  total_discount?: string | number;
  new_total?: string | number;
}

export interface CheckoutSessionStatusResponse extends CheckoutSessionResponse {
  payment_status?: string;
}

export interface CheckActiveSessionResponse {
  hasActiveSession?: boolean;
  has_active_session?: boolean;
  sessionInfo?: ActiveSessionInfo;
  session_info?: ActiveSessionInfo;
}

export interface TicketAvailabilityItem {
  id?: number;
  ticket_id?: number;
  remaining_tickets?: number;
  available?: number;
}

export interface TicketAvailabilityResponse {
  tickets?: TicketAvailabilityItem[];
  availability?: TicketAvailabilityItem[];
  results?: TicketAvailabilityItem[];
}

export interface CheckoutSummary {
  subtotal: number;
  platformFee: number;
  processingFee: number;
  total: number;
}

export function calculateCheckoutSummary(subtotal: number): CheckoutSummary {
  const platformFee = subtotal * PLATFORM_FEE_RATE;
  const processingFee = subtotal * STRIPE_FEE_RATE + STRIPE_FIXED_FEE;
  return {
    subtotal,
    platformFee,
    processingFee,
    total: subtotal + platformFee + processingFee,
  };
}

export function getCheckoutSessionId(response: CheckoutSessionResponse): string | null {
  return response.sessionId ?? response.session_id ?? null;
}

export function getCheckoutClientSecret(response: CheckoutSessionResponse): string | null {
  return response.clientSecret ?? response.client_secret ?? null;
}

export function getCheckoutPublishableKey(response: CheckoutSessionResponse): string | null {
  return response.publishableKey ?? response.publishable_key ?? null;
}

export function getCheckoutActiveSession(
  response: CheckoutSessionResponse | CheckActiveSessionResponse,
): ActiveSessionInfo | null {
  if ('active_session' in response && response.active_session) return response.active_session;
  if ('activeSession' in response && response.activeSession) return response.activeSession;
  if ('sessionInfo' in response && response.sessionInfo) return response.sessionInfo;
  if ('session_info' in response && response.session_info) return response.session_info;
  // Flat conflict response: backend returns the session directly at the root level
  // (detectable because a real checkout success response always has clientSecret)
  const flat = response as Record<string, unknown>;
  if (
    typeof flat.session_id === 'string' &&
    flat.session_id &&
    !flat.clientSecret &&
    !flat.client_secret
  ) {
    return {
      session_id: flat.session_id,
      email: typeof flat.email === 'string' ? flat.email : undefined,
      event_id: typeof flat.event_id === 'number' ? flat.event_id : undefined,
    };
  }
  return null;
}

export function getReservationExpiryDate(response: CheckoutSessionResponse): Date | null {
  if (response.reservationExpiresAt) {
    const date = new Date(response.reservationExpiresAt);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof response.expires_at === 'number') {
    const date = new Date(response.expires_at * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

export function getCheckoutStatusValue(response: CheckoutSessionStatusResponse): string | null {
  return response.status ?? null;
}

export function getCheckoutPaymentStatus(response: CheckoutSessionStatusResponse): string | null {
  return response.payment_status ?? null;
}

export function getCheckoutTotalAmount(response: CheckoutSessionStatusResponse): number {
  return response.total_amount ?? response.amount_total ?? 0;
}

export function storeCheckoutSession(sessionId: string, expiresAt?: string | null): void {
  localStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, sessionId);
  if (expiresAt) {
    localStorage.setItem(CHECKOUT_EXPIRY_STORAGE_KEY, expiresAt);
  }
}

export function storeCheckoutCustomer(details: { email?: string; name?: string }): void {
  if (details.email) {
    localStorage.setItem(CHECKOUT_EMAIL_STORAGE_KEY, details.email);
  }
  if (details.name) {
    localStorage.setItem(CHECKOUT_NAME_STORAGE_KEY, details.name);
  }
}

export function clearStoredCheckoutSession(): void {
  localStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
  localStorage.removeItem(CHECKOUT_EXPIRY_STORAGE_KEY);
}

export function clearStoredCheckoutCustomer(): void {
  localStorage.removeItem(CHECKOUT_EMAIL_STORAGE_KEY);
  localStorage.removeItem(CHECKOUT_NAME_STORAGE_KEY);
}

export function getStoredCheckoutSession(): { sessionId: string | null; expiresAt: string | null } {
  return {
    sessionId: localStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY),
    expiresAt: localStorage.getItem(CHECKOUT_EXPIRY_STORAGE_KEY),
  };
}

export function getStoredCheckoutCustomer(): { email: string; name: string } {
  return {
    email: localStorage.getItem(CHECKOUT_EMAIL_STORAGE_KEY) ?? '',
    name: localStorage.getItem(CHECKOUT_NAME_STORAGE_KEY) ?? '',
  };
}

/**
 * Stripe Embedded Checkout redirects the top-level page to this URL when
 * payment completes, so it must land back on the event checkout page itself
 * (checkoutView === 'success') rather than a separate success page.
 * `{CHECKOUT_SESSION_ID}` is a Stripe template token substituted at redirect time.
 */
export function buildPaymentReturnUrl(eventId?: string | number): string {
  const path = eventId !== undefined ? `/events/${eventId}/checkout` : '/events/public';
  return `${window.location.origin}${path}?session_id={CHECKOUT_SESSION_ID}`;
}

/**
 * Build checkout cancel URL supporting both web and deep linking
 * Web: /checkout/cancel?session_id=xxx
 * Deep Link: musicade://checkout/cancel?session_id=xxx
 */
export function buildCheckoutCancelUrl(
  sessionId: string,
  eventId?: string | number,
  email?: string,
): string {
  const url = new URL('/checkout/cancel', window.location.origin);
  url.searchParams.set('session_id', sessionId);
  if (eventId !== undefined) {
    url.searchParams.set('eventId', String(eventId));
  }
  if (email !== undefined) {
    url.searchParams.set('email', email);
  }
  return url.toString();
}

export async function createPublicCheckoutSession(payload: CreateCheckoutSessionPayload) {
  const { data } = await publicClient.post<CheckoutSessionResponse>('/public/checkout/', payload);
  return data;
}

export async function getPublicCheckoutSessionStatus(sessionId: string) {
  const { data } = await publicClient.get<CheckoutSessionStatusResponse>(
    `/public/checkout/session/${sessionId}/status/`,
  );
  return data;
}

export async function createEmbeddedCheckoutSession(
  eventId: number,
  payload: Omit<CreateCheckoutSessionPayload, 'event_id'>,
) {
  const { data } = await publicClient.post<CheckoutSessionResponse>(
    `/event/${eventId}/embedded-checkout/`,
    payload,
  );
  return data;
}

export async function getEmbeddedCheckoutSessionStatus(sessionId: string) {
  const { data } = await publicClient.get<CheckoutSessionStatusResponse>(
    `/event/checkout-session/${sessionId}/status/`,
  );
  return data;
}

export async function checkActiveCheckoutSession(params: { email?: string; eventUuid?: string }) {
  const search = new URLSearchParams();
  if (params.email) search.set('email', params.email);
  if (params.eventUuid) search.set('event_uuid', params.eventUuid);

  const query = search.toString();
  const { data } = await publicClient.get<CheckActiveSessionResponse>(
    `/event/session/check-active-session/${query ? `?${query}` : ''}`,
  );
  return data;
}

export async function cancelActiveCheckoutSession(payload: {
  email?: string;
  session_id?: string | null;
}) {
  const { data } = await publicClient.post<{ success?: boolean; message?: string }>(
    '/event/session/cancel-active-session/',
    payload,
  );
  return data;
}

export async function resumeCheckoutSession(sessionId: string) {
  const { data } = await publicClient.get<CheckoutSessionResponse>(
    `/event/session/resume-checkout-session/${sessionId}/`,
  );
  return data;
}

export async function fetchTicketAvailability(eventId: number) {
  const { data } = await publicClient.get<TicketAvailabilityResponse>(
    `/event/session/tickets/${eventId}/availability/`,
  );
  return data;
}

export async function createPublicFreeBooking(
  eventUuid: string,
  payload: CreateFreeBookingPayload,
) {
  const { data } = await publicClient.post<FreeBookingResponse>(
    `/event/${eventUuid}/free-bookings/`,
    payload,
  );
  return data;
}

export async function checkFreeBookingEligibility(eventUuid: string, ticketIds: number[]) {
  const { data } = await publicClient.post<FreeBookingEligibilityResponse>(
    `/event/${eventUuid}/free-bookings/eligibility/`,
    { ticket_ids: ticketIds },
  );
  return data;
}

export async function validatePromoCode(
  eventId: number,
  code: string,
  cartItems: PromoCodeCartItem[],
) {
  const { data } = await publicClient.post<PromoCodeValidationResponse>(
    `/event/${eventId}/promo-codes/validate/`,
    { code, cart_items: cartItems },
  );
  return data;
}
